import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { environment, VERSION } from '../../environments/environment';

/**
 * GlitchTip (Sentry-compatible) service for error tracking and monitoring.
 * Sentry is initialized early at bootstrap in main.ts.
 * This service provides typed capture methods and user context management.
 */
@Injectable({ providedIn: 'root' })
export class LazyGlitchTipService {
  /**
   * Set user context after login for better error tracking
   */
  setUserContext(user: { id: string; email?: string }): void {
    if (!environment.production) {
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email,
    });

    Sentry.setTag('authenticated', 'true');
  }

  /**
   * Capture CRM-related errors with specific context
   */
  captureCrmError(
    error: Error,
    context: {
      operation:
        | 'create_deal'
        | 'update_deal'
        | 'delete_deal'
        | 'fetch_deals'
        | 'stage_change'
        | 'analytics'
        | 'search';
      dealId?: string;
      stage?: string;
      subStage?: string;
      searchTerm?: string;
      analyticsType?: string;
      cacheStatus?: string;
      retryAttempt?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('CRM Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'crm');
      scope.setTag('operation', context.operation);

      if (context.stage) {
        scope.setTag('stage', context.stage);
      }

      if (context.cacheStatus) {
        scope.setTag('cache_status', context.cacheStatus);
      }

      scope.setContext('crm_details', {
        dealId: context.dealId,
        stage: context.stage,
        subStage: context.subStage,
        searchTerm: context.searchTerm,
        analyticsType: context.analyticsType,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      // Set level based on operation criticality
      const criticalOperations = ['create_deal', 'update_deal', 'delete_deal'];
      scope.setLevel(criticalOperations.includes(context.operation) ? 'error' : 'warning');

      Sentry.addBreadcrumb({
        category: 'crm',
        message: 'CRM operation failed',
        level: 'error',
        data: {
          operation: context.operation,
          dealId: context.dealId,
          stage: context.stage,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture Firebase/Firestore errors with enhanced context
   */
  captureFirestoreError(
    error: Error,
    context: {
      operation: 'read' | 'write' | 'delete' | 'query' | 'transaction' | 'batch';
      collection: string;
      documentId?: string;
      queryFilters?: Record<string, any>;
      userId?: string;
      retryAttempt?: number;
      offline?: boolean;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Firestore Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'firestore');
      scope.setTag('operation', context.operation);
      scope.setTag('collection', context.collection);

      if (context.offline) {
        scope.setTag('offline', 'true');
      }

      scope.setContext('firestore_details', {
        collection: context.collection,
        documentId: context.documentId,
        queryFilters: context.queryFilters,
        userId: context.userId,
        retryAttempt: context.retryAttempt,
        offline: context.offline,
        ...context.additionalData,
      });

      scope.setLevel('error');

      Sentry.addBreadcrumb({
        category: 'firestore',
        message: 'Firestore operation failed',
        level: 'error',
        data: {
          operation: context.operation,
          collection: context.collection,
          documentId: context.documentId,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture API/HTTP errors with request context
   */
  captureApiError(
    error: Error,
    context: {
      operation: string;
      endpoint: string;
      method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
      statusCode?: number;
      requestData?: any;
      responseData?: any;
      retryAttempt?: number;
      timeout?: boolean;
      networkError?: boolean;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('API Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'api');
      scope.setTag('operation', context.operation);
      scope.setTag('method', context.method);
      scope.setTag('endpoint', context.endpoint);

      if (context.statusCode) {
        scope.setTag('status_code', context.statusCode.toString());
      }

      if (context.timeout) {
        scope.setTag('timeout', 'true');
      }

      if (context.networkError) {
        scope.setTag('network_error', 'true');
      }

      scope.setContext('api_details', {
        endpoint: context.endpoint,
        method: context.method,
        statusCode: context.statusCode,
        requestData: this.sanitizeData(context.requestData),
        responseData: this.sanitizeData(context.responseData),
        retryAttempt: context.retryAttempt,
        timeout: context.timeout,
        networkError: context.networkError,
        ...context.additionalData,
      });

      // Set level based on status code
      const level = context.statusCode && context.statusCode >= 500 ? 'error' : 'warning';
      scope.setLevel(level);

      Sentry.addBreadcrumb({
        category: 'api',
        message: 'API request failed',
        level: level,
        data: {
          method: context.method,
          endpoint: context.endpoint,
          statusCode: context.statusCode,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture authentication errors
   */
  captureAuthError(
    error: Error,
    context: {
      operation: 'login' | 'logout' | 'token_refresh' | 'password_reset' | 'registration';
      provider?: 'email' | 'google' | 'facebook';
      userId?: string;
      errorCode?: string;
      retryAttempt?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Auth Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'authentication');
      scope.setTag('operation', context.operation);

      if (context.provider) {
        scope.setTag('provider', context.provider);
      }

      if (context.errorCode) {
        scope.setTag('error_code', context.errorCode);
      }

      scope.setContext('auth_details', {
        operation: context.operation,
        provider: context.provider,
        userId: context.userId,
        errorCode: context.errorCode,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      scope.setLevel('error');

      Sentry.addBreadcrumb({
        category: 'authentication',
        message: 'Authentication operation failed',
        level: 'error',
        data: {
          operation: context.operation,
          provider: context.provider,
          errorCode: context.errorCode,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture Google Calendar integration errors
   */
  captureCalendarError(
    error: Error,
    context: {
      operation: 'connect' | 'disconnect' | 'fetch_events' | 'auth_callback' | 'token_refresh';
      provider: 'google';
      userId?: string;
      calendarId?: string;
      eventCount?: number;
      cacheHit?: boolean;
      retryAttempt?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Calendar Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'calendar_integration');
      scope.setTag('operation', context.operation);
      scope.setTag('provider', context.provider);

      if (context.cacheHit !== undefined) {
        scope.setTag('cache_hit', context.cacheHit.toString());
      }

      scope.setContext('calendar_details', {
        operation: context.operation,
        provider: context.provider,
        userId: context.userId,
        calendarId: context.calendarId,
        eventCount: context.eventCount,
        cacheHit: context.cacheHit,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      scope.setLevel('warning');

      Sentry.addBreadcrumb({
        category: 'calendar_integration',
        message: 'Calendar operation failed',
        level: 'warning',
        data: {
          operation: context.operation,
          provider: context.provider,
          eventCount: context.eventCount,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture AI service errors
   */
  captureAiError(
    error: Error,
    context: {
      operation: 'classify_image' | 'classify_image_stream' | 'improve_text' | 'spell_check' | 'process_batch';
      model?: string;
      source?: 'bedek' | 'retivut';
      imageCount?: number;
      textLength?: number;
      processingTime?: number;
      retryAttempt?: number;
      apiKeyPresent?: boolean;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('AI Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'ai_service');
      scope.setTag('operation', context.operation);

      if (context.model) {
        scope.setTag('model', context.model);
      }

      if (context.source) {
        scope.setTag('source', context.source);
      }

      if (context.apiKeyPresent !== undefined) {
        scope.setTag('api_key_present', context.apiKeyPresent.toString());
      }

      scope.setContext('ai_details', {
        operation: context.operation,
        model: context.model,
        source: context.source,
        imageCount: context.imageCount,
        textLength: context.textLength,
        processingTime: context.processingTime,
        retryAttempt: context.retryAttempt,
        apiKeyPresent: context.apiKeyPresent,
        ...context.additionalData,
      });

      scope.setLevel('warning');

      Sentry.addBreadcrumb({
        category: 'ai_service',
        message: 'AI service operation failed',
        level: 'warning',
        data: {
          operation: context.operation,
          model: context.model,
          source: context.source,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture network/connection errors
   */
  captureNetworkError(
    error: Error,
    context: {
      operation: 'speed_test' | 'connectivity_check' | 'endpoint_test' | 'ping_endpoint';
      connectionType?: string;
      speed?: number;
      rtt?: number;
      offline?: boolean;
      endpoint?: string;
      retryAttempt?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Network Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'network');
      scope.setTag('operation', context.operation);

      if (context.connectionType) {
        scope.setTag('connection_type', context.connectionType);
      }

      if (context.offline) {
        scope.setTag('offline', 'true');
      }

      scope.setContext('network_details', {
        operation: context.operation,
        connectionType: context.connectionType,
        speed: context.speed,
        rtt: context.rtt,
        offline: context.offline,
        endpoint: context.endpoint,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      scope.setLevel('warning');

      Sentry.addBreadcrumb({
        category: 'network',
        message: 'Network operation failed',
        level: 'warning',
        data: {
          operation: context.operation,
          connectionType: context.connectionType,
          offline: context.offline,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture storage/cache errors
   */
  captureStorageError(
    error: Error,
    context: {
      operation: 'read' | 'write' | 'delete' | 'clear' | 'migrate' | 'init';
      storageType: 'localStorage' | 'indexedDB' | 'memory' | 'ionic_storage';
      key?: string;
      dataSize?: number;
      quotaExceeded?: boolean;
      retryAttempt?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Storage Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'storage');
      scope.setTag('operation', context.operation);
      scope.setTag('storage_type', context.storageType);

      if (context.quotaExceeded) {
        scope.setTag('quota_exceeded', 'true');
      }

      scope.setContext('storage_details', {
        operation: context.operation,
        storageType: context.storageType,
        key: context.key,
        dataSize: context.dataSize,
        quotaExceeded: context.quotaExceeded,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      scope.setLevel(context.quotaExceeded ? 'error' : 'warning');

      Sentry.addBreadcrumb({
        category: 'storage',
        message: 'Storage operation failed',
        level: 'warning',
        data: {
          operation: context.operation,
          storageType: context.storageType,
          key: context.key,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture performance issues
   */
  capturePerformanceIssue(
    message: string,
    context: {
      operation: string;
      duration: number;
      threshold: number;
      component?: string;
      route?: string;
      memoryUsage?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.warn('Performance Issue:', message, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('issue_type', 'performance');
      scope.setTag('operation', context.operation);

      if (context.component) {
        scope.setTag('component', context.component);
      }

      if (context.route) {
        scope.setTag('route', context.route);
      }

      scope.setContext('performance_details', {
        operation: context.operation,
        duration: context.duration,
        threshold: context.threshold,
        component: context.component,
        route: context.route,
        memoryUsage: context.memoryUsage,
        ...context.additionalData,
      });

      scope.setLevel('warning');

      Sentry.addBreadcrumb({
        category: 'performance',
        message: 'Performance issue detected',
        level: 'warning',
        data: {
          operation: context.operation,
          duration: context.duration,
          threshold: context.threshold,
        },
      });

      Sentry.captureMessage(message, 'warning');
    });
  }

  /**
   * Track user interactions and feature usage
   */
  trackUserInteraction(event: {
    action: string;
    feature: string;
    component?: string;
    value?: number;
    properties?: Record<string, any>;
  }): void {
    if (!environment.production) {
      console.log('User Interaction:', event);
      return;
    }

    Sentry.addBreadcrumb({
      category: 'user_interaction',
      message: 'User interaction',
      level: 'info',
      data: {
        action: event.action,
        feature: event.feature,
        component: event.component,
        value: event.value,
        properties: event.properties,
      },
    });
  }

  /**
   * Sanitize sensitive data before sending to GlitchTip
   */
  private sanitizeData(data: any): any {
    if (!data) return data;

    const sensitiveKeys = ['password', 'token', 'key', 'secret', 'auth', 'credential'];

    if (typeof data === 'object') {
      const sanitized = { ...data };
      for (const key of Object.keys(sanitized)) {
        if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof sanitized[key] === 'object') {
          sanitized[key] = this.sanitizeData(sanitized[key]);
        }
      }
      return sanitized;
    }

    return data;
  }

  /**
   * Capture image upload related errors with specific context
   */
  captureImageUploadError(
    error: Error,
    context: {
      operation: 'upload' | 'preload' | 'resize' | 'compress' | 'convert' | 'queue' | 'batch_upload';
      imageCount?: number;
      imageSize?: number;
      imageDimensions?: { width: number; height: number };
      fileType?: string;
      blockIndex?: number;
      imageIndex?: number;
      retryAttempt?: number;
      connectionType?: string;
      source?: 'camera' | 'gallery' | 'file';
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Image Upload Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      // Set specific tags for image operations
      scope.setTag('error_type', 'image_upload');
      scope.setTag('operation', context.operation);
      scope.setTag('source', context.source || 'unknown');

      if (context.connectionType) {
        scope.setTag('connection_type', context.connectionType);
      }

      // Set context data
      scope.setContext('image_details', {
        count: context.imageCount,
        size: context.imageSize,
        dimensions: context.imageDimensions,
        fileType: context.fileType,
        blockIndex: context.blockIndex,
        imageIndex: context.imageIndex,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      // Set level based on operation criticality
      const criticalOperations = ['upload', 'batch_upload'];
      scope.setLevel(criticalOperations.includes(context.operation) ? 'error' : 'warning');

      // Add breadcrumb for the operation
      Sentry.addBreadcrumb({
        category: 'image_upload',
        message: 'Image upload operation failed',
        level: 'error',
        data: {
          operation: context.operation,
          source: context.source,
          imageCount: context.imageCount,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Track image upload performance metrics
   */
  trackImageUploadPerformance(metrics: {
    operation: 'upload' | 'preload' | 'resize' | 'compress' | 'convert' | 'batch_upload';
    duration: number;
    imageCount?: number;
    imageSize?: number;
    success: boolean;
    source?: 'camera' | 'gallery' | 'file';
    connectionType?: string;
    queueSize?: number;
  }): void {
    if (!environment.production) {
      return;
    }

    if (metrics.success) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('metric_type', 'image_upload_performance');
      scope.setTag('operation', metrics.operation);
      scope.setTag('success', 'false');

      if (metrics.connectionType) {
        scope.setTag('connection_type', metrics.connectionType);
      }

      scope.setContext('performance_metrics', {
        duration: metrics.duration,
        imageCount: metrics.imageCount,
        imageSize: metrics.imageSize,
        source: metrics.source,
        queueSize: metrics.queueSize,
      });

      Sentry.addBreadcrumb({
        category: 'image_upload_performance',
        message: 'Image upload performance issue',
        level: 'warning',
        data: {
          duration: metrics.duration,
          success: false,
          operation: metrics.operation,
        },
      });

      Sentry.captureMessage('Image upload operation failed', 'warning');
    });
  }

  /**
   * Track camera-specific errors
   */
  captureCameraError(
    error: Error,
    context: {
      operation: 'init' | 'capture' | 'permissions' | 'switch' | 'flash' | 'zoom';
      deviceInfo?: {
        platform?: string;
        userAgent?: string;
        mediaDevices?: boolean;
      };
      cameraConstraints?: MediaStreamConstraints;
      retryAttempt?: number;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('Camera Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'camera');
      scope.setTag('operation', context.operation);

      if (context.deviceInfo?.platform) {
        scope.setTag('platform', context.deviceInfo.platform);
      }

      scope.setContext('camera_details', {
        operation: context.operation,
        deviceInfo: context.deviceInfo,
        constraints: context.cameraConstraints,
        retryAttempt: context.retryAttempt,
        ...context.additionalData,
      });

      scope.setLevel('error');

      Sentry.addBreadcrumb({
        category: 'camera',
        message: 'Camera operation failed',
        level: 'error',
        data: {
          operation: context.operation,
          retryAttempt: context.retryAttempt,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Track AI classification errors for images
   */
  captureAIClassificationError(
    error: Error,
    context: {
      imageSource?: 'camera' | 'gallery' | 'file';
      imageSize?: number;
      processingTime?: number;
      retryAttempt?: number;
      aiService?: string;
      additionalData?: Record<string, any>;
    },
  ): void {
    if (!environment.production) {
      console.error('AI Classification Error:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'ai_classification');
      scope.setTag('ai_service', context.aiService || 'unknown');

      if (context.imageSource) {
        scope.setTag('image_source', context.imageSource);
      }

      scope.setContext('ai_classification', {
        imageSource: context.imageSource,
        imageSize: context.imageSize,
        processingTime: context.processingTime,
        retryAttempt: context.retryAttempt,
        aiService: context.aiService,
        ...context.additionalData,
      });

      scope.setLevel('warning');

      Sentry.addBreadcrumb({
        category: 'ai_classification',
        message: 'AI image classification failed',
        level: 'warning',
        data: {
          imageSource: context.imageSource,
          processingTime: context.processingTime,
        },
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Track image processing queue events
   */
  trackImageQueueEvent(event: {
    action: string;
    queueType: 'upload' | 'optimization' | 'preload';
    queueSize: number;
    concurrency?: number;
    pendingCount?: number;
    failureRate?: number;
    additionalData?: Record<string, any>;
  }): void {
    if (!environment.production) {
      console.log('Image Queue Event:', event);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('event_type', 'image_queue');
      scope.setTag('queue_action', event.action);
      scope.setTag('queue_type', event.queueType);

      scope.setContext('queue_metrics', {
        action: event.action,
        queueType: event.queueType,
        queueSize: event.queueSize,
        concurrency: event.concurrency,
        pendingCount: event.pendingCount,
        failureRate: event.failureRate,
        ...event.additionalData,
      });

      const level = event.action === 'queue_error' ? 'error' : event.action === 'queue_throttle' ? 'warning' : 'info';

      if (level === 'info') {
        return;
      }

      scope.setLevel(level);

      Sentry.addBreadcrumb({
        category: 'image_queue',
        message: 'Image queue event',
        level: level,
        data: {
          queueSize: event.queueSize,
          action: event.action,
          queueType: event.queueType,
        },
      });

      Sentry.captureMessage('Image queue operation issue', level);
    });
  }

  /**
   * Track IndexedDB health and recovery metrics
   */
  trackIndexedDBHealth(metrics: {
    status: 'healthy' | 'recovering' | 'failed';
    recoveryAttempt?: number;
    maxRecoveryAttempts?: number;
    platform?: string;
    visibilityState?: string;
    networkState?: 'online' | 'offline';
    additionalData?: Record<string, any>;
  }): void {
    if (!environment.production) {
      console.log('IndexedDB Health:', metrics);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('metric_type', 'indexeddb_health');
      scope.setTag('status', metrics.status);

      if (metrics.platform) {
        scope.setTag('platform', metrics.platform);
      }

      if (metrics.networkState) {
        scope.setTag('network_state', metrics.networkState);
      }

      scope.setContext('indexeddb_health', {
        status: metrics.status,
        recoveryAttempt: metrics.recoveryAttempt,
        maxRecoveryAttempts: metrics.maxRecoveryAttempts,
        platform: metrics.platform,
        visibilityState: metrics.visibilityState,
        networkState: metrics.networkState,
        ...metrics.additionalData,
      });

      const level = metrics.status === 'failed' ? 'error' : metrics.status === 'recovering' ? 'warning' : 'info';

      Sentry.addBreadcrumb({
        category: 'indexeddb_health',
        message: 'IndexedDB health check',
        level: level,
        data: {
          status: metrics.status,
          recoveryAttempt: metrics.recoveryAttempt,
        },
      });

      if (metrics.status === 'failed' || metrics.status === 'recovering') {
        Sentry.captureMessage('IndexedDB health issue detected', level);
      }
    });
  }

  /**
   * Track storage recovery events with detailed context
   */
  trackStorageRecovery(event: {
    phase: 'recovery_start' | 'recovery_success' | 'recovery_failed' | 'recovery_waiting';
    operationName: string;
    originalError?: string;
    recoveryDuration?: number;
    previousDriver?: string;
    newDriver?: string;
    connectionAttempts?: number;
    userAgent?: string;
    additionalData?: Record<string, any>;
  }): void {
    if (!environment.production) {
      console.log('Storage Recovery:', event);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('event_type', 'storage_recovery');
      scope.setTag('recovery_phase', event.phase);
      scope.setTag('operation_name', event.operationName);

      if (event.newDriver) {
        scope.setTag('new_driver', event.newDriver);
      }

      scope.setContext('storage_recovery', {
        phase: event.phase,
        operationName: event.operationName,
        originalError: event.originalError,
        recoveryDuration: event.recoveryDuration,
        previousDriver: event.previousDriver,
        newDriver: event.newDriver,
        connectionAttempts: event.connectionAttempts,
        userAgent: event.userAgent,
        timestamp: new Date().toISOString(),
        ...event.additionalData,
      });

      const level = event.phase === 'recovery_failed' ? 'error' : event.phase === 'recovery_start' ? 'warning' : 'info';

      if (event.phase === 'recovery_success') {
        return;
      }

      Sentry.addBreadcrumb({
        category: 'storage_recovery',
        message: 'Storage recovery event',
        level: level,
        data: {
          phase: event.phase,
          operationName: event.operationName,
          recoveryDuration: event.recoveryDuration,
        },
      });

      Sentry.captureMessage('Storage recovery operation', level);
    });
  }

  /**
   * Add a breadcrumb for flow tracking
   */
  addBreadcrumb(breadcrumb: {
    category: string;
    message: string;
    level: 'info' | 'warning' | 'error' | 'debug';
    data?: Record<string, any>;
  }): void {
    if (!environment.production) {
      return;
    }

    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level,
      data: breadcrumb.data,
    });
  }

  /**
   * Generic exception capture for cases where specific methods don't apply
   */
  captureException(
    error: Error,
    options?: {
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      level?: 'error' | 'warning' | 'info' | 'debug';
      fingerprint?: string[];
      user?: { id?: string; email?: string; username?: string };
    },
  ): void {
    if (!environment.production) {
      console.error('Exception:', error, options);
      return;
    }

    Sentry.withScope((scope) => {
      if (options?.tags) {
        Object.entries(options.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      if (options?.extra) {
        scope.setContext('extra', this.sanitizeData(options.extra));
      }

      if (options?.level) {
        scope.setLevel(options.level);
      }

      if (options?.fingerprint) {
        scope.setFingerprint(options.fingerprint);
      }

      if (options?.user) {
        scope.setUser(options.user);
      }

      Sentry.captureException(error);
    });
  }

  /**
   * Generic message capture
   */
  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' | 'debug' = 'info',
    options?: {
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      fingerprint?: string[];
    },
  ): void {
    if (!environment.production) {
      console.log(`[${level.toUpperCase()}]`, message, options);
      return;
    }

    Sentry.withScope((scope) => {
      if (options?.tags) {
        Object.entries(options.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      if (options?.extra) {
        scope.setContext('extra', this.sanitizeData(options.extra));
      }

      if (options?.fingerprint) {
        scope.setFingerprint(options.fingerprint);
      }

      scope.setLevel(level);
      Sentry.captureMessage(message, level);
    });
  }
}
